import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import api from '../utils/axiosConfig';
import './Diaristas.css';

const OPERATIONS = [
  { key: 'Carregamento', label: 'Carregamento (Terminal)', icon: '📦', needsKey: 'needsCarregamento', qtdKey: 'qtdCarregamento', valorKey: 'valorCarregamento', carretasKey: 'carretasCarregamento', hasCarretas: true, carretasLabel: 'Veículos Carregados', hasVolumes: true, volumesKey: 'volumesCarregamento' },
  { key: 'Descarga', label: 'Descarga (Terminal)', icon: '🚛', needsKey: 'needsDescarga', qtdKey: 'qtdDescarga', valorKey: 'valorDescarga', carretasKey: 'carretasDescarga', hasCarretas: true, carretasLabel: 'Veículos Descarregados', hasVolumes: true, volumesKey: 'volumesDescarga' },
  { key: 'Fiscal', label: 'Fiscal (Supervisor)', icon: '👔', needsKey: 'needsFiscal', qtdKey: 'qtdFiscal', valorKey: 'valorFiscal', hasCarretas: false },
  { key: 'Ajudante', label: 'Ajudante de Motorista (Em rota)', icon: '🧑‍✈️', needsKey: 'needsAjudante', qtdKey: 'qtdAjudante', valorKey: 'valorAjudante', hasCarretas: false },
  { key: 'Limpeza', label: 'Limpeza (Interno)', icon: '🧹', needsKey: 'needsLimpeza', qtdKey: 'qtdLimpeza', valorKey: 'valorLimpeza', hasCarretas: false },
  { key: 'CarregamentoExterno', label: 'Carregamento (Cliente Externo)', icon: '🏭', needsKey: 'needsCarregamentoExterno', qtdKey: 'qtdCarregamentoExterno', valorKey: 'valorCarregamentoExterno', hasCarretas: false },
  { key: 'Conferente', label: 'Conferente', icon: '📋', needsKey: 'needsConferente', qtdKey: 'qtdConferente', valorKey: 'valorConferente', hasCarretas: false },
];

const COLORS = ['#4f46e5','#f59e0b','#10b981','#ef4444','#8b5cf6','#06b6d4'];
const PIE_COLORS = ['#4f46e5','#f59e0b'];

const emptyShift = () => {
  const f = { needsDayWorkers: false };
  OPERATIONS.forEach(op => { f[op.needsKey] = false; f[op.qtdKey] = 0; f[op.valorKey] = 0; if (op.carretasKey) f[op.carretasKey] = 0; if (op.volumesKey) f[op.volumesKey] = 0; });
  return f;
};

const emptyForm = () => {
  return { date: new Date().toISOString().split('T')[0], workerType: 'CASA', dia: emptyShift(), noite: emptyShift() };
};

const Diaristas = () => {
  const [activeTab, setActiveTab] = useState('form');
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState('month');
  const [filters, setFilters] = useState({ startDate: '', endDate: '', workerType: '' });
  const [customDates, setCustomDates] = useState({ start: '', end: '' });
  const [activeCustomDates, setActiveCustomDates] = useState({ start: '', end: '' });
  const [phones, setPhones] = useState([]);
  const [phoneForm, setPhoneForm] = useState({ phone: '', name: '', role: 'gerente', selectedCompanyIds: [], allCompanies: false });
  const [companies, setCompanies] = useState([]);
  const [viewAllCompanies, setViewAllCompanies] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const baseCompanyId = user.selectedCompany;
  const queryCompanyId = viewAllCompanies ? 'ALL' : baseCompanyId;

  const showMsg = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg({ text: '', type: '' }), 4000); };

  const fetchRecords = useCallback(async () => {
    try {
      const params = {};
      if (queryCompanyId !== 'ALL') params.companyId = queryCompanyId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.workerType) params.workerType = filters.workerType;
      const res = await api.get('/dayworkers/records', { params });
      setRecords(res.data);
    } catch (e) { console.error(e); }
  }, [queryCompanyId, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = {};
      if (queryCompanyId !== 'ALL') params.companyId = queryCompanyId;
      if (activeCustomDates.start && activeCustomDates.end) {
        params.startDate = activeCustomDates.start;
        params.endDate = activeCustomDates.end;
      }
      const res = await api.get('/dayworkers/summary', { params });
      setSummary(res.data);
    } catch (e) { console.error(e); }
  }, [queryCompanyId, activeCustomDates.start, activeCustomDates.end]);

  const fetchPhones = async () => {
    try { const res = await api.get('/dayworkers/manager-phones'); setPhones(res.data); } catch (e) { console.error(e); }
  };

  const fetchCompanies = async () => {
    try { const res = await api.get('/companies'); setCompanies(res.data); } catch (e) { console.error(e); }
  };

  // Fetch companies on mount so the form always has company names
  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') fetchRecords();
    if (activeTab === 'dashboard') fetchSummary();
    if (activeTab === 'phones') fetchPhones();
  }, [activeTab, fetchRecords, fetchSummary]);

  const [editingShift, setEditingShift] = useState(null); // 'DIA' or 'NOITE'

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleShiftChange = (shift, field, value) => {
    setForm(prev => {
      const updatedShift = { ...prev[shift], [field]: value };
      if (field === 'needsDayWorkers' && !value) {
        OPERATIONS.forEach(op => { updatedShift[op.needsKey] = false; updatedShift[op.qtdKey] = 0; updatedShift[op.valorKey] = 0; if (op.carretasKey) updatedShift[op.carretasKey] = 0; if (op.volumesKey) updatedShift[op.volumesKey] = 0; });
      }
      if (field.startsWith('needs') && field !== 'needsDayWorkers' && !value) {
        const op = OPERATIONS.find(o => o.needsKey === field);
        if (op) { updatedShift[op.qtdKey] = 0; updatedShift[op.valorKey] = 0; if (op.carretasKey) updatedShift[op.carretasKey] = 0; if (op.volumesKey) updatedShift[op.volumesKey] = 0; }
      }
      return { ...prev, [shift]: updatedShift };
    });
  };

  const calcSubtotal = (shiftObj, op) => (shiftObj[op.qtdKey] || 0) * (shiftObj[op.valorKey] || 0);
  const calcTotal = (shiftObj) => OPERATIONS.reduce((sum, op) => sum + (shiftObj[op.needsKey] ? calcSubtotal(shiftObj, op) : 0), 0);
  const calcTotalWorkers = (shiftObj) => OPERATIONS.reduce((sum, op) => sum + (shiftObj[op.needsKey] ? (shiftObj[op.qtdKey] || 0) : 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        // Editing a specific shift
        const payload = { ...form[editingShift], shift: editingShift, date: form.date, workerType: form.workerType, companyId: baseCompanyId };
        await api.put(`/dayworkers/records/${editingId}`, payload);
        showMsg('Registro atualizado com sucesso!');
        setEditingId(null);
        setEditingShift(null);
      } else {
        // Creating new records
        if (!form.dia.needsDayWorkers && !form.noite.needsDayWorkers) {
          showMsg('Selecione pelo menos um turno (Dia ou Noite) com necessidade de diaristas.', 'error');
          return;
        }
        const promises = [];
        if (form.dia.needsDayWorkers) promises.push(api.post('/dayworkers/records', { ...form.dia, shift: 'DIA', date: form.date, workerType: form.workerType, companyId: baseCompanyId }));
        if (form.noite.needsDayWorkers) promises.push(api.post('/dayworkers/records', { ...form.noite, shift: 'NOITE', date: form.date, workerType: form.workerType, companyId: baseCompanyId }));
        
        await Promise.all(promises);
        showMsg('Registro salvo com sucesso!');
      }
      setForm(emptyForm());
    } catch (e) { showMsg(e.response?.data?.message || 'Erro ao salvar.', 'error'); }
  };

  const handleEdit = (record) => {
    const shift = record.shift || 'DIA';
    const f = emptyForm();
    f.date = record.date?.split('T')[0] || '';
    f.workerType = record.workerType;
    f[shift.toLowerCase()] = { ...record };
    delete f[shift.toLowerCase()]._id; delete f[shift.toLowerCase()].__v; delete f[shift.toLowerCase()].createdAt; delete f[shift.toLowerCase()].updatedAt; delete f[shift.toLowerCase()].totalCost; delete f[shift.toLowerCase()].totalWorkers; delete f[shift.toLowerCase()].filledBy; delete f[shift.toLowerCase()].id;
    
    setForm(f);
    setEditingId(record._id);
    setEditingShift(shift);
    setActiveTab('form');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;
    try { await api.delete(`/dayworkers/records/${id}`); showMsg('Registro excluído.'); fetchRecords(); } catch (e) { showMsg('Erro ao excluir.', 'error'); }
  };

  const handleAddPhone = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        phone: phoneForm.phone,
        name: phoneForm.name,
        role: phoneForm.role,
        allCompanies: phoneForm.allCompanies,
        companyIds: phoneForm.allCompanies ? [] : phoneForm.selectedCompanyIds
      };
      if (!payload.allCompanies && payload.companyIds.length === 0) {
        showMsg('Selecione pelo menos uma unidade ou marque "Todas as Unidades".', 'error');
        return;
      }
      await api.post('/dayworkers/manager-phones', payload);
      showMsg('Telefone adicionado!');
      setPhoneForm({ phone: '', name: '', role: 'gerente', selectedCompanyIds: [], allCompanies: false });
      fetchPhones();
    } catch (e) {
      const errorMsg = e.response?.data?.message || e.message || 'Erro desconhecido.';
      showMsg(`Erro: ${errorMsg}`, 'error');
    }
  };

  const handleRemovePhone = async (id) => {
    if (!window.confirm('Remover este telefone?')) return;
    try { await api.delete(`/dayworkers/manager-phones/${id}`); showMsg('Removido!'); fetchPhones(); } catch (e) { showMsg('Erro.', 'error'); }
  };

  const handleTriggerReminders = async () => {
    if (!window.confirm('Deseja disparar os lembretes para os gerentes agora?')) return;
    try { await api.post('/dayworkers/trigger-reminders'); showMsg('Lembretes enviados com sucesso!'); } catch (e) { showMsg('Erro ao enviar lembretes.', 'error'); }
  };

  const handleTriggerSummary = async () => {
    if (!window.confirm('Deseja disparar o resumo matinal para os administradores agora?')) return;
    try { await api.post('/dayworkers/trigger-summary'); showMsg('Resumo enviado com sucesso!'); } catch (e) { showMsg('Erro ao enviar resumo.', 'error'); }
  };

  const getPeriodData = () => { 
    if (!summary) return null; 
    if (period === 'custom') return summary.custom || null;
    return summary[period] || summary.month; 
  };

  const fmtCurrency = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  const renderShiftForm = (shiftKey, label, icon) => {
    const shiftData = form[shiftKey];
    if (editingId && editingShift !== shiftKey.toUpperCase()) return null; // Hide other shift when editing

    return (
      <div className="shift-section" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', marginBottom: '20px', background: shiftKey === 'noite' ? '#f8fafc' : '#fff' }}>
        <div className={`needs-toggle-container ${shiftData.needsDayWorkers ? 'active-yes' : ''}`} style={{ marginBottom: shiftData.needsDayWorkers ? '20px' : '0' }}>
          <span className="toggle-label">{icon} Teve necessidade de diaristas no turno {label}?</span>
          <label className="toggle-switch">
            <input type="checkbox" checked={shiftData.needsDayWorkers} onChange={e => handleShiftChange(shiftKey, 'needsDayWorkers', e.target.checked)} />
            <span className="toggle-slider"></span>
          </label>
          <span style={{ fontWeight: 700, color: shiftData.needsDayWorkers ? '#059669' : '#dc2626' }}>{shiftData.needsDayWorkers ? 'SIM' : 'NÃO'}</span>
        </div>

        {shiftData.needsDayWorkers && (
          <>
            <div className="operations-grid">
              {OPERATIONS.map((op) => (
                <div key={op.key} className={`operation-card ${shiftData[op.needsKey] ? 'active' : ''}`}>
                  <div className="operation-card-header" onClick={() => handleShiftChange(shiftKey, op.needsKey, !shiftData[op.needsKey])}>
                    <div className="op-info">
                      <div className="op-icon">{op.icon}</div>
                      <div className="op-title">{op.label}</div>
                    </div>
                    <label className="toggle-switch" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={shiftData[op.needsKey]} onChange={e => handleShiftChange(shiftKey, op.needsKey, e.target.checked)} />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  {shiftData[op.needsKey] && (
                    <div className="operation-details">
                      <div className={`operation-fields ${op.hasVolumes ? 'four-cols' : (op.hasCarretas ? 'three-cols' : '')}`}>
                        <div className="op-field">
                          <label>Quantidade</label>
                          <input type="number" min="0" value={shiftData[op.qtdKey]} onChange={e => handleShiftChange(shiftKey, op.qtdKey, parseInt(e.target.value) || 0)} />
                        </div>
                        <div className="op-field">
                          <label>Valor Unitário (R$)</label>
                          <input type="number" min="0" step="0.01" value={shiftData[op.valorKey]} onChange={e => handleShiftChange(shiftKey, op.valorKey, parseFloat(e.target.value) || 0)} />
                        </div>
                        {op.hasCarretas && (
                          <div className="op-field">
                            <label>{op.carretasLabel || 'Carretas no Terminal'}</label>
                            <input type="number" min="0" value={shiftData[op.carretasKey]} onChange={e => handleShiftChange(shiftKey, op.carretasKey, parseInt(e.target.value) || 0)} />
                          </div>
                        )}
                        {op.hasVolumes && (
                          <div className="op-field">
                            <label>Volumes</label>
                            <input type="number" min="0" value={shiftData[op.volumesKey]} onChange={e => handleShiftChange(shiftKey, op.volumesKey, parseInt(e.target.value) || 0)} />
                          </div>
                        )}
                      </div>
                      {calcSubtotal(shiftData, op) > 0 && <div className="op-subtotal">Subtotal: {fmtCurrency(calcSubtotal(shiftData, op))}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="total-bar">
              <div className="total-item"><div className="total-label">Total Diaristas ({label})</div><div className="total-value">{calcTotalWorkers(shiftData)}</div></div>
              <div className="total-item"><div className="total-label">Custo Total ({label})</div><div className="total-value cost">{fmtCurrency(calcTotal(shiftData))}</div></div>
            </div>
          </>
        )}
      </div>
    );
  };

  // ===== RENDER FORM =====
  const renderForm = () => (
    <div className="form-section">
      <h2>📝 {editingId ? `Editar Registro (Turno ${editingShift === 'NOITE' ? 'Noite' : 'Dia'})` : 'Novo Registro de Diaristas'}</h2>
      {msg.text && <div className={`diaristas-message ${msg.type}`}>{msg.text}</div>}
      <form onSubmit={handleSubmit}>
        <div className="form-header-row">
          <div className="form-field">
            <label>📅 Data</label>
            <input type="date" value={form.date} onChange={e => handleFormChange('date', e.target.value)} required />
          </div>
          <div className="form-field">
            <label>🏢 Unidade</label>
            <input type="text" value={companies.find(c => (c._id?.toString?.() || c._id) === baseCompanyId)?.name || baseCompanyId || 'Selecione empresa'} disabled />
          </div>
        </div>

        <div className="worker-type-selector">
          {[{ value: 'CASA', icon: '🏠', label: 'Da Casa', desc: 'Funcionário interno' }, { value: 'TERCEIRO', icon: '🤝', label: 'Terceiro', desc: 'Mão de obra externa' }].map(t => (
            <div key={t.value} className={`type-option ${form.workerType === t.value ? 'selected' : ''}`} onClick={() => handleFormChange('workerType', t.value)}>
              <div className="type-icon">{t.icon}</div>
              <div className="type-label">{t.label}</div>
              <div className="type-desc">{t.desc}</div>
            </div>
          ))}
        </div>

        {renderShiftForm('dia', 'DIA', '☀️')}
        {renderShiftForm('noite', 'NOITE', '🌙')}

        <div className="form-submit-container">
          <button type="button" className="btn-clear" onClick={() => { setForm(emptyForm()); setEditingId(null); setEditingShift(null); }}>Limpar</button>
          <button type="submit" className="btn-submit">{editingId ? '✏️ Atualizar' : '💾 Salvar Registros'}</button>
        </div>
      </form>
    </div>
  );

  // ===== RENDER DASHBOARD =====
  const renderDashboard = () => {
    const data = getPeriodData();
    if (!summary) return <div className="empty-state"><div className="empty-icon">📊</div><p>Carregando dados...</p></div>;

    const opLabels = { carregamento: 'Carregamento', descarga: 'Descarga', fiscal: 'Fiscal', ajudante: 'Ajudante', limpeza: 'Limpeza', carregamentoExterno: 'Carga Ext.', conferente: 'Conferente' };
    
    const opEntries = data ? Object.entries(data.byOperation || {}) : [];
    const barData = opEntries.map(([k, v]) => ({ name: opLabels[k] || k, diaristas: v.workers, custo: v.cost }));
    
    let maiorOfensor = { name: '-', cost: 0 };
    opEntries.forEach(([k, v]) => { if(v.cost > maiorOfensor.cost) maiorOfensor = { name: opLabels[k] || k, cost: v.cost } });

    const pieCostData = opEntries.filter(([k, v]) => v.cost > 0).map(([k, v]) => ({ name: opLabels[k] || k, value: v.cost }));
    const pieData = data ? [{ name: 'Casa', value: data.casaWorkers || 0 }, { name: 'Terceiro', value: data.terceiroWorkers || 0 }].filter(d => d.value > 0) : [];
    const lineData = data?.dailyTrend || [];
    
    const custoMedio = data?.totalWorkers > 0 ? (data.totalCost / data.totalWorkers) : 0;

    return (
      <div className="dashboard-section">
        <div className="period-selector">
          {[{ k: 'today', l: '📆 Hoje' }, { k: 'week', l: '📅 Semana' }, { k: 'month', l: '🗓️ Mês' }, { k: 'prevMonth', l: '⏪ Mês Ant.' }, { k: 'custom', l: '⚙️ Personalizado' }].map(p => (
            <button key={p.k} className={`period-btn ${period === p.k ? 'active' : ''}`} onClick={() => setPeriod(p.k)}>{p.l}</button>
          ))}
        </div>
        {period === 'custom' && (
          <div className="custom-period-selector" style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '25px', alignItems: 'flex-end' }}>
            <div className="form-field" style={{ flex: 'none', minWidth: '150px' }}>
              <label style={{ fontSize: '0.8em' }}>Data Inicial</label>
              <input type="date" value={customDates.start} onChange={e => setCustomDates(p => ({ ...p, start: e.target.value }))} style={{ padding: '10px' }} />
            </div>
            <div className="form-field" style={{ flex: 'none', minWidth: '150px' }}>
              <label style={{ fontSize: '0.8em' }}>Data Final</label>
              <input type="date" value={customDates.end} onChange={e => setCustomDates(p => ({ ...p, end: e.target.value }))} style={{ padding: '10px' }} />
            </div>
            <button className="btn-submit" style={{ padding: '10px 20px', height: '42px' }} onClick={() => setActiveCustomDates(customDates)}>🔍 Filtrar</button>
          </div>
        )}

        <div className="kpi-grid">
          <div className="kpi-card"><div className="kpi-icon">👷</div><div className="kpi-value">{data?.totalWorkers || 0}</div><div className="kpi-label">Total Diaristas</div></div>
          <div className="kpi-card highlight"><div className="kpi-icon">💰</div><div className="kpi-value">{fmtCurrency(data?.totalCost)}</div><div className="kpi-label">Gasto Total</div></div>
          <div className="kpi-card" style={{borderColor: '#8b5cf6'}}><div className="kpi-icon">💸</div><div className="kpi-value">{fmtCurrency(custoMedio)}</div><div className="kpi-label">Custo Médio / Diarista</div></div>
          <div className="kpi-card"><div className="kpi-icon">🏠</div><div className="kpi-value">{data?.casaWorkers || 0}</div><div className="kpi-label">Da Casa</div><div className="kpi-sub">{fmtCurrency(data?.casaCost)}</div></div>
          <div className="kpi-card"><div className="kpi-icon">🤝</div><div className="kpi-value">{data?.terceiroWorkers || 0}</div><div className="kpi-label">Terceiros</div><div className="kpi-sub">{fmtCurrency(data?.terceiroCost)}</div></div>
        </div>

        <div className="charts-grid">
          <div className="chart-card">
            <h3>📊 Diaristas por Operação</h3>
            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={11} /><YAxis /><Tooltip formatter={(v, n) => n === 'custo' ? fmtCurrency(v) : v} /><Legend /><Bar dataKey="diaristas" fill="#4f46e5" radius={[4,4,0,0]} /><Bar dataKey="custo" fill="#10b981" radius={[4,4,0,0]} /></BarChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>Sem dados no período</p></div>}
          </div>
          <div className="chart-card">
            <h3>🍕 Distribuição Financeira</h3>
            {pieCostData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart><Pie data={pieCostData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieCostData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie><Tooltip formatter={(v) => fmtCurrency(v)} /></PieChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>Sem dados</p></div>}
          </div>
        </div>

        <div className="charts-grid three-cols">
          <div className="chart-card">
            <h3>📈 Evolução Diária</h3>
            {lineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={lineData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="date" fontSize={11} tickFormatter={d => { const parts = d.split('-'); return `${parts[2]}/${parts[1]}`; }} /><YAxis yAxisId="left" /><YAxis yAxisId="right" orientation="right" /><Tooltip formatter={(v, n) => n === 'cost' ? fmtCurrency(v) : v} labelFormatter={d => { const parts = d.split('-'); return `${parts[2]}/${parts[1]}/${parts[0]}`; }} /><Legend /><Line yAxisId="left" type="monotone" dataKey="workers" stroke="#4f46e5" name="Diaristas" strokeWidth={2} /><Line yAxisId="right" type="monotone" dataKey="cost" stroke="#10b981" name="Custo (R$)" strokeWidth={2} /></LineChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>Sem dados</p></div>}
          </div>
          <div className="chart-card">
            <h3>🍩 Casa vs Terceiro</h3>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart><Pie data={pieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie><Tooltip /></PieChart>
              </ResponsiveContainer>
            ) : <div className="empty-state"><p>Sem dados</p></div>}
          </div>
        </div>

        {data?.byCompany && data.byCompany.length > 0 && (
          <div className="company-table-container">
            <h3>🏢 Resumo por Unidade</h3>
            <table className="company-summary-table">
              <thead><tr><th>Unidade</th><th>Diaristas</th><th>Custo Total</th></tr></thead>
              <tbody>{data.byCompany.sort((a,b) => b.cost - a.cost).map((c, i) => (
                <tr key={i}><td>{c.companyName}</td><td>{c.workers}</td><td className="cost-cell">{fmtCurrency(c.cost)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ===== RENDER HISTORY =====
  const renderHistory = () => (
    <div className="history-section">
      <div className="history-filters">
        <div className="form-field"><label>De</label><input type="date" value={filters.startDate} onChange={e => setFilters(p => ({ ...p, startDate: e.target.value }))} /></div>
        <div className="form-field"><label>Até</label><input type="date" value={filters.endDate} onChange={e => setFilters(p => ({ ...p, endDate: e.target.value }))} /></div>
        <div className="form-field"><label>Tipo</label>
          <select value={filters.workerType} onChange={e => setFilters(p => ({ ...p, workerType: e.target.value }))}>
            <option value="">Todos</option><option value="CASA">Da Casa</option><option value="TERCEIRO">Terceiro</option>
          </select>
        </div>
        <button className="btn-submit" style={{ padding: '12px 24px' }} onClick={fetchRecords}>🔍 Filtrar</button>
      </div>

      {records.length > 0 ? (
        <div className="history-table-container">
          <table className="history-table">
            <thead><tr><th>Data</th><th>Turno</th><th>Tipo</th><th>Diaristas</th><th>Custo</th><th>Volumes</th><th>Carregamento</th><th>Descarga</th><th>Fiscal</th><th>Ajudante</th><th>Limpeza</th><th>Carga Ext.</th><th>Conferente</th>{user.role === 'Administrador' && <th>Ações</th>}</tr></thead>
            <tbody>{records.map(r => (
              <tr key={r._id}>
                <td>{r.date ? new Date(r.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</td>
                <td><span style={{fontWeight: 'bold', color: r.shift === 'NOITE' ? '#312e81' : '#d97706'}}>{r.shift === 'NOITE' ? '🌙 Noite' : '☀️ Dia'}</span></td>
                <td><span className={`badge ${r.workerType === 'CASA' ? 'badge-casa' : 'badge-terceiro'}`}>{r.workerType === 'CASA' ? '🏠 Casa' : '🤝 Terceiro'}</span></td>
                <td><strong>{r.totalWorkers || 0}</strong></td>
                <td><strong style={{ color: '#059669' }}>{fmtCurrency(r.totalCost)}</strong></td>
                <td><strong>{(r.volumesCarregamento || 0) + (r.volumesDescarga || 0)}</strong></td>
                <td>{r.needsCarregamento ? r.qtdCarregamento : '-'}</td>
                <td>{r.needsDescarga ? r.qtdDescarga : '-'}</td>
                <td>{r.needsFiscal ? r.qtdFiscal : '-'}</td>
                <td>{r.needsAjudante ? r.qtdAjudante : '-'}</td>
                <td>{r.needsLimpeza ? r.qtdLimpeza : '-'}</td>
                <td>{r.needsCarregamentoExterno ? r.qtdCarregamentoExterno : '-'}</td>
                <td>{r.needsConferente ? r.qtdConferente : '-'}</td>
                {user.role === 'Administrador' && (
                  <td><div className="history-actions">
                    <button className="btn-edit-sm" onClick={() => handleEdit(r)}>✏️</button>
                    <button className="btn-delete-sm" onClick={() => handleDelete(r._id)}>🗑️</button>
                  </div></td>
                )}
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <div className="empty-state"><div className="empty-icon">📋</div><p>Nenhum registro encontrado.</p></div>}
    </div>
  );

  const toggleCompanySelection = (companyId) => {
    setPhoneForm(prev => {
      const ids = prev.selectedCompanyIds.includes(companyId)
        ? prev.selectedCompanyIds.filter(id => id !== companyId)
        : [...prev.selectedCompanyIds, companyId];
      return { ...prev, selectedCompanyIds: ids };
    });
  };

  // ===== RENDER PHONES =====
  const renderPhones = () => (
    <div className="phones-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
        <div>
          <h3 style={{ marginBottom: '8px' }}>📱 Telefones para Notificações</h3>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '0.9em' }}>
            <strong>Gerente:</strong> Recebe lembrete às 17:30 para preencher o formulário.<br/>
            <strong>Admin Diarista:</strong> Recebe resumo matinal às 07:20 com totais.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-clear" style={{ fontSize: '0.85em', padding: '10px 16px', color: '#1d4ed8', borderColor: '#bfdbfe', background: '#eff6ff', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={handleTriggerReminders}><span>🔔</span> Enviar Lembretes</button>
          <button className="btn-clear" style={{ fontSize: '0.85em', padding: '10px 16px', color: '#d97706', borderColor: '#fde68a', background: '#fffbeb', display: 'flex', alignItems: 'center', gap: '6px' }} onClick={handleTriggerSummary}><span>📊</span> Enviar Resumo</button>
        </div>
      </div>
      {msg.text && <div className={`diaristas-message ${msg.type}`}>{msg.text}</div>}
      <form onSubmit={handleAddPhone}>
        <div className="phone-form-row">
          <div className="form-field"><label>Nome</label><input type="text" value={phoneForm.name} onChange={e => setPhoneForm(p => ({ ...p, name: e.target.value }))} placeholder="Nome do contato" /></div>
          <div className="form-field"><label>Telefone *</label><input type="text" value={phoneForm.phone} onChange={e => setPhoneForm(p => ({ ...p, phone: e.target.value }))} placeholder="+5511999999999" required /></div>
          <div className="form-field"><label>Função *</label>
            <select value={phoneForm.role} onChange={e => setPhoneForm(p => ({ ...p, role: e.target.value }))}>
              <option value="gerente">🔔 Gerente (Lembrete 17:30)</option>
              <option value="admin_diarista">📊 Admin (Resumo 07:20)</option>
            </select>
          </div>
        </div>

        {/* Unit Selector */}
        <div className="unit-selector-container">
          <label className="unit-selector-label">🏢 Unidades *</label>
          <div className="unit-all-toggle" onClick={() => setPhoneForm(p => ({ ...p, allCompanies: !p.allCompanies, selectedCompanyIds: [] }))}>
            <label className="unit-checkbox">
              <input type="checkbox" checked={phoneForm.allCompanies} onChange={() => {}} />
              <span className="unit-checkmark"></span>
            </label>
            <span className="unit-all-label">🌐 Todas as Unidades</span>
          </div>
          {!phoneForm.allCompanies && (
            <div className="unit-list">
              {companies.filter(c => !c.name?.toUpperCase().includes('ACLOG')).map(c => {
                const cId = c._id?.toString?.() || c._id;
                const isChecked = phoneForm.selectedCompanyIds.includes(cId);
                return (
                  <div key={cId} className={`unit-item ${isChecked ? 'selected' : ''}`} onClick={() => toggleCompanySelection(cId)}>
                    <label className="unit-checkbox">
                      <input type="checkbox" checked={isChecked} onChange={() => {}} />
                      <span className="unit-checkmark"></span>
                    </label>
                    <span className="unit-name">{c.name}</span>
                  </div>
                );
              })}
            </div>
          )}
          {phoneForm.allCompanies && (
            <div className="unit-all-info">Este contato receberá notificações de todas as unidades cadastradas.</div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button type="submit" className="btn-submit" style={{ padding: '12px 28px' }}>➕ Adicionar</button>
        </div>
      </form>

      <div className="phone-list" style={{ marginTop: '24px' }}>
        {phones.length > 0 ? phones.map(p => (
          <div key={p._id} className="phone-item">
            <div className="phone-info">
              <span className="phone-number">{p.name ? `${p.name} — ` : ''}{p.phone}</span>
              <span className="phone-meta">
                <span className={`phone-role-badge ${p.role}`}>{p.role === 'gerente' ? '🔔 Gerente' : '📊 Admin Diarista'}</span>
                {p.allCompanies ? (
                  <span className="phone-unit-badge all">🌐 Todas as Unidades</span>
                ) : (p.companyNames && p.companyNames.length > 0) ? (
                  p.companyNames.map((name, i) => (
                    <span key={i} className="phone-unit-badge">{name}</span>
                  ))
                ) : (
                  <span className="phone-unit-badge none">⚠️ Sem unidade</span>
                )}
              </span>
            </div>
            <button className="btn-remove-phone" onClick={() => handleRemovePhone(p._id)}>🗑️</button>
          </div>
        )) : <div className="empty-state"><p>Nenhum telefone cadastrado.</p></div>}
      </div>
    </div>
  );

  // ===== MAIN RENDER =====
  return (
    <div className="diaristas-container">
      <div className="diaristas-tabs">
        <button className={`diaristas-tab ${activeTab === 'form' ? 'active' : ''}`} onClick={() => setActiveTab('form')}><span className="tab-icon">📝</span> Formulário</button>
        <button className={`diaristas-tab ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}><span className="tab-icon">📊</span> Dashboard</button>
        <button className={`diaristas-tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}><span className="tab-icon">📋</span> Histórico</button>
        {user.role === 'Administrador' && <button className={`diaristas-tab ${activeTab === 'phones' ? 'active' : ''}`} onClick={() => setActiveTab('phones')}><span className="tab-icon">📱</span> Telefones</button>}
      </div>

      {user.role === 'Administrador' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
          <button 
            className="btn-submit" 
            style={{ padding: '8px 16px', background: viewAllCompanies ? '#10b981' : '#6b7280', fontSize: '0.9em', display: 'flex', alignItems: 'center', gap: '8px' }}
            onClick={() => setViewAllCompanies(!viewAllCompanies)}
          >
            {viewAllCompanies ? '🌐 Vendo Todas Unidades' : '🏢 Ver Todas Unidades'}
          </button>
        </div>
      )}

      {activeTab === 'form' && renderForm()}
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'history' && renderHistory()}
      {activeTab === 'phones' && renderPhones()}
    </div>
  );
};

export default Diaristas;
